import React, { ReactNode, useMemo, useState } from 'react';
import { Card } from '../../../components/common';
import AppointmentsHeader from './AppointmentsHeader/AppointmentsHeader';
import { DateOnlyISOString, todayInIso } from '../../../utils/dateUtil';
import { CardLoader } from '../../../components/common/Loading';
import { AppointmentTimes } from './AppointmentTimes';
import { DnaAndMiaDialog } from './DnaAndMiaDialog';
import { AppointmentCardControl } from './SlotAppointment';
import { UseStaffAppointmentsType } from '../../../hooks/staff/useAppointmentsFlow';
import { AppointmentsPrintHeader } from './AppointmentsPrintHeader/AppointmentsPrintHeader';
import { AppointmentsPrintArea } from './StaffAppointmentsCard.styled';

type IStaffAppointmentsProps = {
  style?: React.CSSProperties;
  onOverflowClicked?: () => void;
  topElement?: ReactNode;
  printable?: boolean;
} & AppointmentCardControl &
  UseStaffAppointmentsType;

export const StaffAppointmentsCard: React.FunctionComponent<
  IStaffAppointmentsProps
> = ({
  setOrToggleEditAppointmentId,
  editAppointmentId,
  style,
  onOverflowClicked,
  topElement,
  printable = false,
  ...useStaffAppointmentsData
}) => {
  const [dnaMiaOpen, setDnaMiaOpen] = useState(false);
  const {
    isFetching: appointmentsFetching,
    setSelectedDate,
    selectedDate,
    isUpdate,
    changeUpdateMode,
    appointmentsData,
    refetch,
  } = useStaffAppointmentsData;

  const filteredAppointments = useMemo(() => {
    return appointmentsData?.data?.filter(
      ({ status }) =>
        status !== 'FREE' &&
        status !== 'CANCELLED' &&
        status !== 'LOCKED' &&
        status !== 'EXPIRED',
    );
  }, [appointmentsData]);

  const isFetching = appointmentsFetching;

  return (
    <Card fillHeight={isFetching} style={style}>
      {topElement}
      <AppointmentsHeader
        selectedDate={selectedDate}
        setSelectedDate={(date) =>
          date ? setSelectedDate(date) : setSelectedDate(todayInIso())
        }
        isUpdate={isUpdate}
        setIsUpdate={changeUpdateMode}
        onDnaMiaClicked={() => {
          setDnaMiaOpen(true);
        }}
        onRefreshClicked={() => refetch()}
        onOverflowClicked={onOverflowClicked}
        showPrintButton={printable}
      />
      {isFetching ? (
        <CardLoader
          style={{
            marginTop: 'var(--s4)',
          }}
        />
      ) : (
        <AppointmentTimesContent
          printable={printable}
          selectedDate={selectedDate}
        >
          <AppointmentTimes
            setOrToggleEditAppointmentId={setOrToggleEditAppointmentId}
            editAppointmentId={editAppointmentId}
            isUpdate={isUpdate}
            appointments={filteredAppointments}
          />
        </AppointmentTimesContent>
      )}
      {dnaMiaOpen && (
        <DnaAndMiaDialog
          appointments={filteredAppointments}
          onCloseClicked={() => setDnaMiaOpen(false)}
        />
      )}
    </Card>
  );
};

const AppointmentTimesContent = ({
  children,
  printable,
  selectedDate,
}: {
  children: ReactNode;
  printable: boolean;
  selectedDate: DateOnlyISOString;
}) => {
  return printable ? (
    <AppointmentsPrintArea>
      <AppointmentsPrintHeader selectedDate={selectedDate} />
      {children}
    </AppointmentsPrintArea>
  ) : (
    <>{children}</>
  );
};
